import {useEffect} from "react";
import {FieldError, useFormContext, useWatch} from "react-hook-form";
import {css} from "@emotion/react";

import {calculateRemSize, mb, p} from "@pg-design/helpers-css-strings";
import {CheckBoxGroupControlled, RadioGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {handleRtkqApiError, isFetchBaseQueryError} from "@pg-mono/react-hook-forms-utils";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";

import {getAdditionalAreaName} from "../../../property/utils/get_additional_area_type";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {FOR_YOU_ADDITIONAL_FORM_LABELS} from "../../../user/constants/preferences/application_preferences_labels";
import {useUpdateForYouClientMutation} from "../../api/for_you_client";
import {additionalAreasOptions, dateOptions, floorOptions, kitchenOptions, quartersOptions} from "../../constants/additional_form_options";
import {setForYouAdditional} from "../../state/for_you_modal_slice";
import {ForYouAdditionalGtmEvent, ForYouFormDisplayGtmEvent} from "../../tracking/for_you_forms_gtm_events";
import {constructionEndDateName} from "../../types/ConstructionEndDateType";
import {ForYouDisplayActions} from "../../types/ForYouDisplayActions";
import {ForYouEventType} from "../../types/ForYouEventType";
import {IForYouForm} from "../../types/IForYouForm";
import {SubmitHandlerType} from "../../types/SubmitHandlerType";
import {getFloorString} from "../../utils/get_floor_string";
import {getKitchenTypeName} from "../../utils/get_kitchen_type_name";
import {getQuartersString} from "../../utils/get_quarters_string";
import {getUpdateData} from "../../utils/get_update_data";
import {ForYouModalButton} from "../modal_parts/ForYouModalButton";

interface IProps {
    submitHandler: SubmitHandlerType;
    onNextStep: () => void;
    onSubmitError?: (name: string, error: FieldError) => void;
}

const buttonText = "Zapisz";

export const ForYouAdditionalForm = (props: IProps) => {
    const dispatch = useAppDispatch();

    const modalState = useAppSelector((state) => state.forYouModal);

    const {setError} = useFormContext<IForYouForm>();

    const additional_areas = useWatch({name: "additional_areas"});
    const kitchen = useWatch({name: "kitchen"});
    const floor = useWatch({name: "floor"});
    const date = useWatch({name: "date"});
    const quarters = useWatch({name: "quarters"});
    const message = useWatch({name: "comments"});

    const [updateClient, {isSuccess, isError, error, isLoading}] = useUpdateForYouClientMutation();

    useEffect(() => {
        ForYouFormDisplayGtmEvent({event: ForYouEventType.ADDITIONAL, action: ForYouDisplayActions.OPEN, region: modalState.mainRegionName});
    }, []);

    useEffect(() => {
        if (isSuccess) {
            const areasString = getAdditionalAreaName(parseInt(additional_areas));
            const kitchenType = getKitchenTypeName(kitchen);
            const dateString = constructionEndDateName(parseInt(date));
            const quartersString = getQuartersString(quarters);
            const floorString = getFloorString(floor);
            ForYouAdditionalGtmEvent({additionalAreas: areasString, kitchenType, floor: floorString, quarters: quartersString, date: dateString, message});
            props.onNextStep();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError && isFetchBaseQueryError(error)) {
            handleRtkqApiError({error, setError, handle400ValidationError: true});
        }
    }, [isError, error]);

    const onSubmit = async () => {
        const updateClientPayload = {additional_areas, kitchen, floor, date, quarters, comments: message};
        dispatch(setForYouAdditional(updateClientPayload));

        const postData = getUpdateData({
            ...modalState,
            additional: {
                ...modalState.additional,
                ...updateClientPayload
            }
        });

        // API will throw validation error if comments field is present but empty.
        if (!message) {
            delete postData.comments;
        }

        await updateClient(postData);
    };

    return (
        <form onSubmit={props.submitHandler(onSubmit)} css={formStyle}>
            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.additional_areas}
                </Text>

                <RadioGroupControlled name="additional_areas" options={additionalAreasOptions} className="list-style" optionClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.kitchen}
                </Text>

                <RadioGroupControlled name="kitchen" options={kitchenOptions} className="list-style" optionClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.floor}
                </Text>

                <CheckBoxGroupControlled name="floor" options={floorOptions} className="list-style" checkBoxClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.quarters}
                </Text>

                <CheckBoxGroupControlled name="quarters" options={quartersOptions} className="list-style" checkBoxClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.date}
                </Text>

                <RadioGroupControlled
                    name="date"
                    options={dateOptions}
                    className="list-style long-list-style"
                    optionClassName="option-style long-list-option-style"
                />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.message}
                </Text>

                <TextareaField
                    name="comments"
                    placeholder="Napisz, na co zwrócić szczególną uwagę podczas poszukiwań nieruchomości."
                    testid={FOR_YOU_TEST_IDS.ADDITIONAL_MESSAGE}
                />
            </div>

            <ForYouModalButton buttonText={buttonText} isLoading={isLoading} dataTestId={FOR_YOU_TEST_IDS.SAVE_BUTTON} />
        </form>
    );
};

const formStyle = css`
    width: 100%;
    background-color: ${getThemeVariable("colors.gray.100")};
    ${p(3, 1.5, 0, 1.5)};

    .filed-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: ${calculateRemSize(2)};
        ${mb(2)};
        ${p(2)};
        background-color: #fff;
        border-radius: ${calculateRemSize(1)};
        p {
            white-space: nowrap;
        }
    }

    .list-style {
        display: flex;
        gap: ${calculateRemSize(3)};
        flex-direction: column;

        @media (min-width: ${getThemeBreakpoint().md}) {
            flex-direction: row;
            row-gap: ${calculateRemSize(1)};
        }
    }

    .long-list-style {
        flex-wrap: wrap;
    }

    .option-style {
        flex: 0;
        flex-direction: row;
    }

    .long-list-option-style {
        flex-basis: 20%;
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3, 5, 3, 5)};
    }
`;
import {useEffect} from "react";
import {FieldError, useFormContext, useWatch} from "react-hook-form";
import {css} from "@emotion/react";

import {calculateRemSize, mb, p} from "@pg-design/helpers-css-strings";
import {CheckBoxGroupControlled, RadioGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {handleRtkqApiError, isFetchBaseQueryError} from "@pg-mono/react-hook-forms-utils";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";

import {getAdditionalAreaName} from "../../../property/utils/get_additional_area_type";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {FOR_YOU_ADDITIONAL_FORM_LABELS} from "../../../user/constants/preferences/application_preferences_labels";
import {useUpdateForYouClientMutation} from "../../api/for_you_client";
import {additionalAreasOptions, dateOptions, floorOptions, kitchenOptions, quartersOptions} from "../../constants/additional_form_options";
import {setForYouAdditional} from "../../state/for_you_modal_slice";
import {ForYouAdditionalGtmEvent, ForYouFormDisplayGtmEvent} from "../../tracking/for_you_forms_gtm_events";
import {constructionEndDateName} from "../../types/ConstructionEndDateType";
import {ForYouDisplayActions} from "../../types/ForYouDisplayActions";
import {ForYouEventType} from "../../types/ForYouEventType";
import {IForYouForm} from "../../types/IForYouForm";
import {SubmitHandlerType} from "../../types/SubmitHandlerType";
import {getFloorString} from "../../utils/get_floor_string";
import {getKitchenTypeName} from "../../utils/get_kitchen_type_name";
import {getQuartersString} from "../../utils/get_quarters_string";
import {getUpdateData} from "../../utils/get_update_data";
import {ForYouModalButton} from "../modal_parts/ForYouModalButton";

interface IProps {
    submitHandler: SubmitHandlerType;
    onNextStep: () => void;
    onSubmitError?: (name: string, error: FieldError) => void;
}

const buttonText = "Zapisz";

export const ForYouAdditionalForm = (props: IProps) => {
    const dispatch = useAppDispatch();

    const modalState = useAppSelector((state) => state.forYouModal);

    const {setError} = useFormContext<IForYouForm>();

    const additional_areas = useWatch({name: "additional_areas"});
    const kitchen = useWatch({name: "kitchen"});
    const floor = useWatch({name: "floor"});
    const date = useWatch({name: "date"});
    const quarters = useWatch({name: "quarters"});
    const message = useWatch({name: "comments"});

    const [updateClient, {isSuccess, isError, error, isLoading}] = useUpdateForYouClientMutation();

    useEffect(() => {
        ForYouFormDisplayGtmEvent({event: ForYouEventType.ADDITIONAL, action: ForYouDisplayActions.OPEN, region: modalState.mainRegionName});
    }, []);

    useEffect(() => {
        if (isSuccess) {
            const areasString = getAdditionalAreaName(parseInt(additional_areas));
            const kitchenType = getKitchenTypeName(kitchen);
            const dateString = constructionEndDateName(parseInt(date));
            const quartersString = getQuartersString(quarters);
            const floorString = getFloorString(floor);
            ForYouAdditionalGtmEvent({additionalAreas: areasString, kitchenType, floor: floorString, quarters: quartersString, date: dateString, message});
            props.onNextStep();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError && isFetchBaseQueryError(error)) {
            handleRtkqApiError({error, setError, handle400ValidationError: true});
        }
    }, [isError, error]);

    const onSubmit = async () => {
        const updateClientPayload = {additional_areas, kitchen, floor, date, quarters, comments: message};
        dispatch(setForYouAdditional(updateClientPayload));

        const postData = getUpdateData({
            ...modalState,
            additional: {
                ...modalState.additional,
                ...updateClientPayload
            }
        });

        // API will throw validation error if comments field is present but empty.
        if (!message) {
            delete postData.comments;
        }

        await updateClient(postData);
    };

    return (
        <form onSubmit={props.submitHandler(onSubmit)} css={formStyle}>
            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.additional_areas}
                </Text>

                <RadioGroupControlled name="additional_areas" options={additionalAreasOptions} className="list-style" optionClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.kitchen}
                </Text>

                <RadioGroupControlled name="kitchen" options={kitchenOptions} className="list-style" optionClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.floor}
                </Text>

                <CheckBoxGroupControlled name="floor" options={floorOptions} className="list-style" checkBoxClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.quarters}
                </Text>

                <CheckBoxGroupControlled name="quarters" options={quartersOptions} className="list-style" checkBoxClassName="option-style" />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.date}
                </Text>

                <RadioGroupControlled
                    name="date"
                    options={dateOptions}
                    className="list-style long-list-style"
                    optionClassName="option-style long-list-option-style"
                />
            </div>

            <div className="filed-wrapper">
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    {FOR_YOU_ADDITIONAL_FORM_LABELS.message}
                </Text>

                <TextareaField
                    name="comments"
                    placeholder="Napisz, na co zwrócić szczególną uwagę podczas poszukiwań nieruchomości."
                    testid={FOR_YOU_TEST_IDS.ADDITIONAL_MESSAGE}
                />
            </div>

            <ForYouModalButton buttonText={buttonText} isLoading={isLoading} dataTestId={FOR_YOU_TEST_IDS.SAVE_BUTTON} />
        </form>
    );
};

const formStyle = css`
    width: 100%;
    background-color: ${getThemeVariable("colors.gray.100")};
    ${p(3, 1.5, 0, 1.5)};

    .filed-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: ${calculateRemSize(2)};
        ${mb(2)};
        ${p(2)};
        background-color: #fff;
        border-radius: ${calculateRemSize(1)};
        p {
            white-space: nowrap;
        }
    }

    .list-style {
        display: flex;
        gap: ${calculateRemSize(3)};
        flex-direction: column;

        @media (min-width: ${getThemeBreakpoint().md}) {
            flex-direction: row;
            row-gap: ${calculateRemSize(1)};
        }
    }

    .long-list-style {
        flex-wrap: wrap;
    }

    .option-style {
        flex: 0;
        flex-direction: row;
    }

    .long-list-option-style {
        flex-basis: 20%;
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3, 5, 3, 5)};
    }
`;
